import { createReducer } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  addAIFashionVideoResult,
  cancelAIFashion,
  deleteAIFashionResult,
  enterAIFashionEntry,
  resetAIFashionState,
  setAIFashionAccessDecision,
  setAIFashionError,
  setAIFashionPoseTiles,
  setAIFashionPostGenerationPrompt,
  setAIFashionReady,
  setAIFashionResultQuality,
  setAIFashionSelectedResultId,
  setAIFashionSessionParams,
  setVideoLoadingAfterId,
  startAIFashionLoading,
  upsertAIFashionResult,
} from './actions'
import type {
  AIFashionImageResultRecord,
  AIFashionResultRecord,
  AIFashionResultSource,
  AIFashionSessionParams,
  AIFashionState,
  AIFashionVideoResultRecord,
} from './types'
import type { AIModelsAccessDecision } from '@/services/ai/aiAccessPolicy'
import type { GeneratedObjectRecord, PoseTile } from '@/scenes/engine/utils/aiFashionSession'

export const initialAIFashionSession: AIFashionSessionParams = {
  selectedTemplate: null,
  promptMetadata: null,
  backgroundPromptOverride: null,
  poseOverride: null,
  compositionOverride: null,
  poseSelectedTileKey: null,
  aspectRatioOption: 'portrait',
  productImageCacheKey: null,
  productRepoEntry: null,
  metadataGenderKey: null,
  metadataProductKey: null,
  lastGeneratedObjectId: null,
  videoResolution: '480p',
  videoDuration: 4,
}

export function createInitialAIFashionState(): AIFashionState {
  return {
    session: { ...initialAIFashionSession },
    results: [],
    selectedResultId: null,
    poseTiles: [],
    poseTilesLoading: false,
    poseTilesLoadingMore: false,
    status: { kind: 'idle' },
    access: null,
    loadingResultAfterId: null,
    videoLoadingAfterId: null,
    postGenerationPrompt: null,
  }
}

export const initialAIFashionState: AIFashionState = createInitialAIFashionState()

function toResultRecord(
  objectId: string,
  record: GeneratedObjectRecord,
  source?: AIFashionResultSource,
): AIFashionImageResultRecord {
  return {
    objectId,
    kind: record.kind,
    result: record.result,
    generationParams: record.generationParams,
    source: source ?? (record.kind === 'generated' ? 'aiFashion' : null),
  }
}

function getFallbackSelectedResultId(results: AIFashionResultRecord[], deletedIndex: number): string | null {
  return results[deletedIndex]?.objectId ?? results[deletedIndex - 1]?.objectId ?? null
}

function getLastGeneratedResultId(results: AIFashionResultRecord[]): string | null {
  for (let index = results.length - 1; index >= 0; index--) {
    const result = results[index]
    if (result.kind === 'generated') {
      return result.objectId
    }
  }

  return null
}

function getLastResultId(results: AIFashionResultRecord[]): string | null {
  return results[results.length - 1]?.objectId ?? null
}

function getLoadingResultAfterId(state: AIFashionState): string | null {
  return state.selectedResultId ?? state.session.productImageCacheKey ?? getLastResultId(state.results)
}

function getInsertIndexAfterResult(results: AIFashionResultRecord[], resultId: string | null): number {
  if (!resultId) return results.length
  const targetIndex = results.findIndex(result => result.objectId === resultId)
  return targetIndex >= 0 ? targetIndex + 1 : results.length
}

export const aiFashionReducer = createReducer(initialAIFashionState, builder => {
  builder
    .addCase(resetAIFashionState, () => createInitialAIFashionState())
    .addCase(cancelAIFashion, state => {
      state.status = { kind: 'idle' }
      state.poseTilesLoading = false
      state.poseTilesLoadingMore = false
      state.loadingResultAfterId = null
      state.postGenerationPrompt = null
    })
    .addCase(setAIFashionSessionParams, (state, { payload }: PayloadAction<Partial<AIFashionSessionParams>>) => {
      state.session = { ...state.session, ...payload }
    })
    .addCase(enterAIFashionEntry, state => {
      state.status = { kind: 'entry' }
      state.loadingResultAfterId = null
      state.postGenerationPrompt = null
    })
    .addCase(startAIFashionLoading, (state, { payload }) => {
      state.status = { kind: 'loading', abortableSince: payload.now }
      state.loadingResultAfterId = getLoadingResultAfterId(state)
      state.postGenerationPrompt = null
    })
    .addCase(setAIFashionReady, state => {
      state.status = { kind: 'ready' }
      state.loadingResultAfterId = null
    })
    .addCase(setAIFashionError, (state, { payload }) => {
      state.status = { kind: 'error', message: payload }
      state.loadingResultAfterId = null
    })
    .addCase(setAIFashionAccessDecision, (state, { payload }: PayloadAction<AIModelsAccessDecision | null>) => {
      state.access = payload
    })
    .addCase(upsertAIFashionResult, (state, { payload }) => {
      const nextRecord = toResultRecord(payload.objectId, payload.record, payload.source)
      const existingIndex = state.results.findIndex(result => result.objectId === payload.objectId)
      if (existingIndex >= 0) {
        state.results[existingIndex] = nextRecord
      } else {
        const insertIndex = nextRecord.kind === 'generated'
          ? getInsertIndexAfterResult(state.results, state.loadingResultAfterId)
          : state.results.length
        state.results.splice(insertIndex, 0, nextRecord)
      }
      if (nextRecord.kind === 'generated') {
        state.selectedResultId = payload.objectId
        state.session.lastGeneratedObjectId = payload.objectId
      }
    })
    .addCase(deleteAIFashionResult, (state, { payload }) => {
      const deletedIndex = state.results.findIndex(result => result.objectId === payload)
      if (deletedIndex < 0) return

      state.results.splice(deletedIndex, 1)
      if (state.selectedResultId === payload) {
        state.selectedResultId = getFallbackSelectedResultId(state.results, deletedIndex)
      }
      if (state.session.lastGeneratedObjectId === payload) {
        state.session.lastGeneratedObjectId = getLastGeneratedResultId(state.results)
      }
    })
    .addCase(setAIFashionSelectedResultId, (state, { payload }) => {
      state.selectedResultId = payload
    })
    .addCase(setAIFashionPoseTiles, (state, { payload }: PayloadAction<{ tiles: PoseTile[]; loading?: boolean; loadingMore?: boolean }>) => {
      state.poseTiles = payload.tiles
      if (payload.loading !== undefined) state.poseTilesLoading = payload.loading
      if (payload.loadingMore !== undefined) state.poseTilesLoadingMore = payload.loadingMore
    })
    .addCase(setAIFashionPostGenerationPrompt, (state, { payload }: PayloadAction<string | null>) => {
      state.postGenerationPrompt = payload
    })
    .addCase(setAIFashionResultQuality, (state, { payload }) => {
      const record = state.results.find(r => r.objectId === payload.resultId)
      if (!record || record.kind === 'video') return
      record.activeQuality = payload.quality
      if (payload.hdEntry) {
        if (!record.qualityResults) record.qualityResults = {}
        record.qualityResults[payload.quality] = payload.hdEntry
      }
    })
    .addCase(addAIFashionVideoResult, (state, { payload }) => {
      const videoRecord: AIFashionVideoResultRecord = {
        objectId: payload.objectId,
        kind: 'video',
        videoUrl: payload.videoUrl,
        sourceResultId: payload.sourceResultId,
        resolution: payload.resolution,
        duration: payload.duration,
      }
      const insertIndex = getInsertIndexAfterResult(state.results, payload.insertAfterId ?? payload.sourceResultId)
      state.results.splice(insertIndex, 0, videoRecord)
      state.selectedResultId = payload.objectId
      state.videoLoadingAfterId = null
      // Remember these params as the session default for next generation
      state.session.videoResolution = payload.resolution
      state.session.videoDuration = payload.duration
    })
    .addCase(setVideoLoadingAfterId, (state, { payload }) => {
      state.videoLoadingAfterId = payload
    })
})
